import type {
  AssetRename,
  QuestionDefinition,
  TestedAssetBlock,
} from "@digital-twins/shared-types";
import { assetRenames } from "@digital-twins/shared-types";

import {
  getConceptAssets,
  renameConceptAssetMentions,
} from "../assets/conceptAssets";

type JsonRecord = Record<string, unknown>;

export interface RebasedDraft<T> {
  draft: T;
  /**
   * Places where the user's unsaved edit and the newer server draft changed
   * the same thing. The server's version is kept there; the caller says so.
   */
  conflicts: number;
}

/**
 * Rebase the builder's unsaved edits onto a newer accepted draft.
 *
 * `base` is the accepted draft the edits started from, `local` the builder's
 * copy, `server` the newer accepted draft (a chat turn, a compile, or the
 * draft a 409 handed back). Whatever only one side changed is taken from that
 * side. Where both changed the same thing, the server's version is kept and
 * counted as a conflict, so the caller can tell the user.
 *
 * Units are chosen so linked fields never split:
 * - a question (matched by `stableKey`) keeps its answer and routing fields as
 *   one structural group, while independently editable presentation fields
 *   merge separately;
 * - an asset (matched by `id`) merges field by field, so the server's analysis
 *   lands next to text the user typed;
 * - any other top-level field is taken whole.
 * A question kept from the local side is re-pointed at any asset the server
 * renamed, so its @mentions name the asset as it is now (the server derives
 * the refs from them when the question is saved).
 *
 * Write timestamps (`updatedAt`) never count as a change.
 */
export function rebaseQuestionnaireDraft<T extends object>(
  base: T,
  local: T,
  server: T,
): RebasedDraft<T> {
  const counter = { conflicts: 0 };
  const baseRecord = base as unknown as JsonRecord;
  const localRecord = local as unknown as JsonRecord;
  const serverRecord = server as unknown as JsonRecord;
  const merged: JsonRecord = {};
  for (const key of unionKeys(serverRecord, localRecord)) {
    if (key === "researchQuestions") continue;
    if (key === "testedAssets") {
      merged[key] = mergeAssetBlock(
        baseRecord[key],
        localRecord[key],
        serverRecord[key],
        counter,
      );
      continue;
    }
    const value = mergeWhole(
      { has: key in baseRecord, value: baseRecord[key] },
      { has: key in localRecord, value: localRecord[key] },
      { has: key in serverRecord, value: serverRecord[key] },
      counter,
      key !== "updatedAt",
    );
    if (value.has) merged[key] = value.value;
  }
  if (
    "researchQuestions" in localRecord ||
    "researchQuestions" in serverRecord
  ) {
    merged.researchQuestions = mergeQuestions(
      baseRecord,
      localRecord,
      serverRecord,
      merged.testedAssets,
      counter,
    );
  }
  // Keep the server's key order so an unchanged result reads as unchanged.
  const ordered: JsonRecord = {};
  for (const key of unionKeys(serverRecord, merged))
    if (key in merged) ordered[key] = merged[key];
  return { draft: ordered as unknown as T, conflicts: counter.conflicts };
}

/**
 * A draft's content as a string for comparison: keys sorted, write
 * timestamps and empty lists left out. Two drafts with the same content
 * compare equal whatever their key order or save time.
 */
export function draftContentFingerprint(value: unknown): string {
  return JSON.stringify(value, function (this: unknown, key, inner) {
    if (key === "updatedAt") return undefined;
    // An empty list on an object says the same as no list.
    if (Array.isArray(inner) && inner.length === 0 && isRecord(this))
      return undefined;
    return isRecord(inner)
      ? Object.fromEntries(
          Object.keys(inner)
            .sort()
            .map((entry) => [entry, inner[entry]]),
        )
      : inner;
  });
}

type Slot = { has: boolean; value: unknown };

function isRecord(value: unknown): value is JsonRecord {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function same(a: unknown, b: unknown): boolean {
  return draftContentFingerprint(a) === draftContentFingerprint(b);
}

function unionKeys(first: JsonRecord, second: JsonRecord): Array<string> {
  return [
    ...Object.keys(first),
    ...Object.keys(second).filter((key) => !(key in first)),
  ];
}

function changed(slot: Slot, base: Slot): boolean {
  return slot.has !== base.has || (slot.has && !same(slot.value, base.value));
}

/** One unit taken whole from one side; the server's when both changed it. */
function mergeWhole(
  base: Slot,
  local: Slot,
  server: Slot,
  counter: { conflicts: number },
  countConflict = true,
): Slot {
  const localChanged = changed(local, base);
  const serverChanged = changed(server, base);
  if (!localChanged) return server;
  if (!serverChanged) return local;
  if (local.has === server.has && (!local.has || same(local.value, server.value)))
    return server;
  if (countConflict) counter.conflicts += 1;
  return server;
}

/** Field by field, recursing into nested objects; arrays are taken whole. */
function mergeFields(
  base: JsonRecord,
  local: JsonRecord,
  server: JsonRecord,
  counter: { conflicts: number },
): JsonRecord {
  const out: JsonRecord = {};
  for (const key of unionKeys(server, local)) {
    const b = { has: key in base, value: base[key] };
    const l = { has: key in local, value: local[key] };
    const s = { has: key in server, value: server[key] };
    if (
      isRecord(b.value) &&
      isRecord(l.value) &&
      isRecord(s.value) &&
      changed(l, b) &&
      changed(s, b)
    ) {
      out[key] = mergeFields(b.value, l.value, s.value, counter);
      continue;
    }
    const value = mergeWhole(b, l, s, counter, key !== "updatedAt");
    if (value.has) out[key] = value.value;
  }
  return out;
}

/**
 * Items matched by a key, in order. The order is the side that changed it;
 * when both reordered, the server's, with the user's additions after it.
 */
function mergeKeyedList<Item>(
  base: Array<Item>,
  local: Array<Item>,
  server: Array<Item>,
  keyOf: (item: Item) => string,
  mergeItem: (b: Item, l: Item, s: Item) => { item: Item; fromLocal: boolean },
  counter: { conflicts: number },
): Array<{ item: Item; fromLocal: boolean }> {
  const byKey = (items: Array<Item>) =>
    new Map(items.map((item) => [keyOf(item), item]));
  const baseBy = byKey(base);
  const localBy = byKey(local);
  const serverBy = byKey(server);
  const keys = (items: Array<Item>) => items.map(keyOf);
  const localReordered = !same(
    keys(local).filter((key) => baseBy.has(key) && serverBy.has(key)),
    keys(base).filter((key) => localBy.has(key) && serverBy.has(key)),
  );
  const serverReordered = !same(
    keys(server).filter((key) => baseBy.has(key) && localBy.has(key)),
    keys(base).filter((key) => localBy.has(key) && serverBy.has(key)),
  );
  if (localReordered && serverReordered) counter.conflicts += 1;
  const primary = localReordered && !serverReordered ? local : server;
  const secondary = primary === local ? server : local;
  const primaryKeys = new Set(keys(primary));
  const order = [
    ...keys(primary),
    ...keys(secondary).filter((key) => !primaryKeys.has(key)),
  ];
  const out: Array<{ item: Item; fromLocal: boolean }> = [];
  for (const key of order) {
    const b = baseBy.get(key);
    const l = localBy.get(key);
    const s = serverBy.get(key);
    if (l && s) {
      out.push(b ? mergeItem(b, l, s) : { item: s, fromLocal: false });
      if (!b && !same(l, s)) counter.conflicts += 1;
    } else if (l) {
      if (!b) out.push({ item: l, fromLocal: true });
      // Removed on the server: the user's edit to it is a conflict, and the
      // server's removal stands.
      else if (!same(l, b)) counter.conflicts += 1;
    } else if (s) {
      if (!b) out.push({ item: s, fromLocal: false });
      // Removed locally while the server changed it: keep the server's.
      else if (!same(s, b)) {
        counter.conflicts += 1;
        out.push({ item: s, fromLocal: false });
      }
    }
  }
  return out;
}

function mergeAssetBlock(
  base: unknown,
  local: unknown,
  server: unknown,
  counter: { conflicts: number },
): unknown {
  if (!isRecord(local) || !isRecord(server))
    return mergeWhole(
      { has: base !== undefined, value: base },
      { has: local !== undefined, value: local },
      { has: server !== undefined, value: server },
      counter,
    ).value;
  const baseBlock = isRecord(base) ? base : {};
  const assetsOf = (block: JsonRecord) =>
    Array.isArray(block.assets) ? (block.assets as Array<JsonRecord>) : [];
  const { assets: _b, ...baseFields } = baseBlock;
  const { assets: _l, ...localFields } = local;
  const { assets: _s, ...serverFields } = server;
  const fields = mergeFields(baseFields, localFields, serverFields, counter);
  const assets = mergeKeyedList(
    assetsOf(baseBlock),
    assetsOf(local),
    assetsOf(server),
    (asset) => String(asset.id),
    (b, l, s) => ({ item: mergeFields(b, l, s, counter), fromLocal: false }),
    counter,
  ).map(({ item }) => item);
  return { ...fields, assets };
}

function mergeQuestions(
  base: JsonRecord,
  local: JsonRecord,
  server: JsonRecord,
  mergedAssets: unknown,
  counter: { conflicts: number },
): Array<QuestionDefinition> {
  const list = (record: JsonRecord) =>
    Array.isArray(record.researchQuestions)
      ? (record.researchQuestions as Array<QuestionDefinition>)
      : [];
  const merged = mergeKeyedList(
    list(base),
    list(local),
    list(server),
    (question) => question.stableKey,
    (b, l, s) => mergeQuestion(b, l, s, counter),
    counter,
  );
  if (!isRecord(mergedAssets)) return merged.map(({ item }) => item);
  const block = mergedAssets as unknown as TestedAssetBlock;
  // The server's rename rule (`assetRenames`): only a mention that follows its
  // renamed asset moves; one a price version kept, or one every version was
  // renamed away from, keeps its words, as the Save leaves it.
  const renames = assetRenames(
    getConceptAssets((base.testedAssets as TestedAssetBlock | undefined) ?? undefined),
    getConceptAssets(block),
  ).filter((rename) => rename.mention === "follows");
  return merged.map(({ item, fromLocal }) =>
    fromLocal && renames.length > 0 ? repointQuestion(item, renames) : item,
  );
}

/**
 * Merge only the fields a manual question edit can own. The answer shape and
 * routing rules form one unit: splitting either from a concurrent type or
 * option change can leave a rule pointing at values that no longer exist.
 *
 * The accepted draft owns ids, asset refs, plan/report attribution and every
 * other derived field. A title, description, required flag or awareness list
 * can safely land beside a concurrent structural update, and vice versa.
 */
function mergeQuestion(
  base: QuestionDefinition,
  local: QuestionDefinition,
  server: QuestionDefinition,
  counter: { conflicts: number },
): { item: QuestionDefinition; fromLocal: boolean } {
  const baseRecord = base as unknown as JsonRecord;
  const localRecord = local as unknown as JsonRecord;
  const serverRecord = server as unknown as JsonRecord;
  const merged: JsonRecord = { ...serverRecord };
  let fromLocal = false;

  for (const key of [
    "title",
    "description",
    "required",
    "awarenessEntities",
  ]) {
    const value = mergeWhole(
      { has: key in baseRecord, value: baseRecord[key] },
      { has: key in localRecord, value: localRecord[key] },
      { has: key in serverRecord, value: serverRecord[key] },
      counter,
    );
    if (value.has) merged[key] = value.value;
    else delete merged[key];
    fromLocal ||=
      value.has &&
      value.value === localRecord[key] &&
      !same(value.value, serverRecord[key]);
  }

  const structuralFields = [
    "type",
    "response",
    "visibilityRule",
    "routingRule",
  ];
  const baseStructural = structuralFields.map((key) => baseRecord[key]);
  const localStructural = structuralFields.map((key) => localRecord[key]);
  const serverStructural = structuralFields.map((key) => serverRecord[key]);
  const localChanged = !same(localStructural, baseStructural);
  const serverChanged = !same(serverStructural, baseStructural);
  const sameStructural = same(localStructural, serverStructural);
  if (localChanged && (!serverChanged || sameStructural)) {
    for (const key of structuralFields) {
      if (key in localRecord) merged[key] = localRecord[key];
      else delete merged[key];
    }
    fromLocal ||= !sameStructural;
  } else if (localChanged && serverChanged) {
    counter.conflicts += 1;
  }

  return { item: merged as unknown as QuestionDefinition, fromLocal };
}

/**
 * Point an unsaved question's @mentions and choice labels at the assets'
 * current names, when another writer renamed them under the edit (R5). Refs
 * are derived on the server from these mentions when the question is saved.
 */
export function repointQuestion(
  question: QuestionDefinition,
  renames: Array<AssetRename>,
): QuestionDefinition {
  return renames.reduce(
    (current, rename) =>
      renameConceptAssetMentions(current, rename.id, rename.from, rename.to),
    question,
  );
}
